import type { Person } from '@local/common-lib';
import { useMediaQuery, useTheme } from '@mui/material';
import * as Sentry from '@sentry/react';
import { useSnackbar } from 'notistack';
import { type FC, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAxios } from '../../hooks/use-axios.js';
import { useAuth, useAuthenticated } from '../../utils/auth/index.js';
import { OrganizationSwitcherView } from './organization-switcher.view.js';

export const OrganizationSwitcher: FC = () => {
  const auth = useAuth();
  const { userInfo } = useAuthenticated();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [busy, setBusy] = useState(false);
  const theme = useTheme();
  // same breakpoint the layout uses to switch to its small-screen app bar
  const compact = useMediaQuery(theme.breakpoints.down('sm'));

  // Whenever the session's organization changes - switched here, or in
  // another tab, which shares the session - leave the current page. A form
  // loaded under the old organization would otherwise save into the new one.
  const previousOrganizationId = useRef(userInfo.organizationId);
  useEffect(() => {
    const previous = previousOrganizationId.current;
    previousOrganizationId.current = userInfo.organizationId;
    if (previous === undefined || previous === userInfo.organizationId) return;
    navigate('/');
  }, [navigate, userInfo.organizationId]);

  const [organizations] = useAxios<Person.MyOrganization[]>(
    '/api/v1/my/organizations',
    undefined,
    // a super admin acts in no organization, so there is nothing to switch
    { condition: !userInfo.isSuperAdmin },
  );

  if (userInfo.isSuperAdmin || organizations.loading || organizations.error) {
    return null;
  }

  const list = organizations.data ?? [];
  // The token is what actually scopes the session, and it changes on a switch
  // while this list does not re-fetch - so trust the claim over the flag, or
  // the app bar would keep naming the organization we just left.
  const current =
    list.find((organization) => organization._id === userInfo.organizationId) ??
    list.find((organization) => organization.current);

  const select = (organizationId: string): void => {
    setBusy(true);
    auth
      .switchOrganization(organizationId)
      .catch((error: Error) => {
        Sentry.captureException(error);
        enqueueSnackbar('Could not switch organization', { variant: 'error' });
      })
      .finally(() => setBusy(false));
  };

  return (
    <OrganizationSwitcherView
      busy={busy}
      compact={compact}
      current={current}
      onSelect={select}
      organizations={list}
    />
  );
};
